import { ILesson, ILessonContentRelease } from '@dtos/Course'
import { IEnrollment } from '@dtos/Enrollment'
import {
  IContentAvailableProps,
  isContentAvailable,
  releaseContentDateText,
} from '@utils/contentAvailable'

export interface ILessonExtraContentAvailability {
  isAvailable: boolean
  text: string
}

type LessonSchedule = Pick<
  ILesson,
  | 'releaseAt'
  | 'releaseAfter'
  | 'lockAt'
  | 'lockAfter'
  | 'courseClassReleaseOffset'
  | 'extraMediaReleases'
>

type EnrollmentAnchor = Pick<IEnrollment, 'activatedAt' | 'courseClassRelease'>

export function findLessonContentRelease(
  lesson: Pick<ILesson, 'extraMediaReleases'>,
  mediaId: number | string,
): ILessonContentRelease | undefined {
  return lesson.extraMediaReleases?.find(
    (release) => String(release.mediaId) === String(mediaId),
  )
}

/**
 * Disponibilidade de um conteúdo adicional dentro da aula. O item nunca abre
 * antes da aula: a aula bloqueada vence, e só depois vale a liberação própria
 * do item (lesson_content_releases no core, que recusa o arquivo no servidor
 * com a mesma regra).
 *
 * A liberação do item é ancorada na ativação da matrícula, como a da aula, e
 * não usa o modo turma: o offset de turma é da aula, não do conteúdo.
 */
export function lessonExtraContentAvailability(
  lesson: LessonSchedule,
  mediaId: number | string,
  enrollment: EnrollmentAnchor | null | undefined,
): ILessonExtraContentAvailability {
  const createdAt = enrollment?.activatedAt ?? ''
  const lessonProps: IContentAvailableProps = {
    releaseAt: lesson.releaseAt,
    releaseAfter: lesson.releaseAfter,
    lockAt: lesson.lockAt,
    lockAfter: lesson.lockAfter,
    createdAt,
    courseClassRelease: enrollment?.courseClassRelease,
    courseClassReleaseOffset: lesson.courseClassReleaseOffset,
  }
  const lessonText = releaseContentDateText(lessonProps)

  if (!isContentAvailable(lessonProps)) {
    return { isAvailable: false, text: lessonText }
  }

  const release = findLessonContentRelease(lesson, mediaId)
  if (!release) return { isAvailable: true, text: lessonText }

  const itemProps: IContentAvailableProps = {
    releaseAt: release.releaseAt,
    releaseAfter: release.releaseAfter,
    lockAt: release.lockAt,
    lockAfter: release.lockAfter,
    createdAt,
  }

  return {
    isAvailable: isContentAvailable(itemProps),
    text: releaseContentDateText(itemProps) || lessonText,
  }
}
